import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Plus, Search, Clock, Users, ClipboardList, 
  Eye, Play, CheckCircle2, XCircle, Trash2, Mail, UserPlus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getScheduledAudits,
  createScheduledAudit,
  deleteScheduledAudit,
  startAudit,
  inviteAuditor,
  getAvailableUsers,
  AUDIT_STATUS,
  AUDIT_TYPES,
  AUDITOR_ROLES,
  getStatusBadge
} from '@/services/scheduledAudit';
import { getFrameworks } from '@/services/grc';
import { toast } from 'sonner';

const ScheduleAuditPage = () => {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [frameworks, setFrameworks] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Create Audit Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    audit_type: 'internal',
    framework_id: '',
    scheduled_date: '',
    create_engagement: false
  });
  
  // Invite Auditor Dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [inviteType, setInviteType] = useState('existing'); // 'existing' or 'external'
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [inviteData, setInviteData] = useState({
    auditor_email: '',
    auditor_name: '',
    auditor_user_id: '',
    role: 'auditor',
    is_external: false
  });

  useEffect(() => {
    loadAudits();
    loadFrameworks();
  }, [search, statusFilter]);

  const loadAudits = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const data = await getScheduledAudits(params);
      setAudits(data.audits || []);
    } catch (error) {
      toast.error('Failed to load scheduled audits');
    } finally {
      setLoading(false);
    }
  };

  const loadFrameworks = async () => {
    // Only show frameworks the tenant has enabled. Owners/admins can enable
    // more frameworks from Compliance → Frameworks first, then schedule an
    // audit against them here.
    try {
      const enabled = await getFrameworks({ page_size: 100 });
      const enabledList = enabled.items || enabled.frameworks || [];
      setFrameworks(enabledList.map((fw) => ({ ...fw, is_enabled: true })));
    } catch (e) {
      console.warn('getFrameworks failed:', e?.message || e);
      setFrameworks([]);
    }
  };

  const loadAvailableUsers = async (searchTerm = '') => {
    try {
      const data = await getAvailableUsers(searchTerm);
      setAvailableUsers(data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleCreateAudit = async () => {
    if (!formData.title || !formData.framework_id || !formData.scheduled_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const result = await createScheduledAudit({
        ...formData,
        scheduled_date: new Date(formData.scheduled_date).toISOString()
      });
      toast.success('Audit scheduled successfully');
      setShowCreateDialog(false);
      resetForm();
      loadAudits();
      
      // Ask to invite auditor
      setSelectedAudit(result.audit);
      setShowInviteDialog(true);
    } catch (error) {
      toast.error('Failed to schedule audit');
    }
  };

  const handleDeleteAudit = async (audit) => {
    if (window.confirm(`Delete scheduled audit "${audit.title}"?`)) {
      try {
        await deleteScheduledAudit(audit.id);
        toast.success('Audit deleted');
        loadAudits();
      } catch (error) {
        toast.error('Failed to delete audit');
      }
    }
  };

  const handleStartAudit = async (audit) => {
    try {
      await startAudit(audit.id);
      toast.success('Audit started');
      loadAudits();
    } catch (error) {
      toast.error('Failed to start audit');
    }
  };

  const handleInviteAuditor = async () => {
    if (!inviteData.auditor_email) {
      toast.error('Please provide auditor email');
      return;
    }

    try {
      await inviteAuditor(selectedAudit.id, {
        ...inviteData,
        is_external: inviteType === 'external'
      });
      toast.success('Auditor invited successfully');
      setShowInviteDialog(false);
      resetInviteForm();
      loadAudits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite auditor');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      audit_type: 'internal',
      framework_id: '',
      scheduled_date: '',
      create_engagement: false
    });
  };

  const resetInviteForm = () => {
    setInviteData({
      auditor_email: '',
      auditor_name: '',
      auditor_user_id: '',
      role: 'auditor',
      is_external: false
    });
    setInviteType('existing');
    setUserSearch('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getProgressColor = (reviewed, total) => {
    if (total === 0) return 'bg-gray-200';
    const percentage = (reviewed / total) * 100;
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-blue-600" />
            Schedule Audit
          </h1>
          <p className="text-gray-600 mt-1">Schedule framework audits and invite auditors</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Schedule New Audit
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search audits..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {AUDIT_STATUS.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Audits List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading audits...</p>
            </CardContent>
          </Card>
        ) : audits.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No scheduled audits found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateDialog(true)}
              >
                Schedule Your First Audit
              </Button>
            </CardContent>
          </Card>
        ) : (
          audits.map(audit => {
            const statusBadge = getStatusBadge(audit.status);
            const progress = audit.total_controls > 0 
              ? Math.round((audit.reviewed_controls / audit.total_controls) * 100) 
              : 0;

            return (
              <Card key={audit.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-900">{audit.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          audit.audit_type === 'external' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {audit.audit_type === 'external' ? 'External' : 'Internal'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          {statusBadge.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 font-mono">{audit.audit_id}</p>
                      {audit.description && (
                        <p className="text-sm text-gray-500 mt-2">{audit.description}</p>
                      )}
                      
                      <div className="flex items-center gap-6 mt-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="h-4 w-4" />
                          <span>{audit.framework_name || 'Framework'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(audit.scheduled_date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>{audit.audit_team?.length || 0} auditors</span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Review Progress</span>
                          <span className="font-medium">{audit.reviewed_controls || 0} / {audit.total_controls} controls ({progress}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getProgressColor(audit.reviewed_controls, audit.total_controls)}`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        {audit.controls_with_issues > 0 && (
                          <p className="text-xs text-red-600 mt-1">
                            {audit.controls_with_issues} controls with issues found
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      {audit.status === 'draft' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleStartAudit(audit)}
                          className="flex items-center gap-1"
                        >
                          <Play className="h-4 w-4" />
                          Start
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedAudit(audit);
                          loadAvailableUsers();
                          setShowInviteDialog(true);
                        }}
                        className="flex items-center gap-1"
                      >
                        <UserPlus className="h-4 w-4" />
                        Invite
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => navigate(`/grc/audit/scheduled/${audit.id}/review`)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        Review
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDeleteAudit(audit)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Audit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Schedule New Audit
            </DialogTitle>
            <DialogDescription>
              Schedule a framework audit and invite auditors to review controls
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Audit Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Q1 2025 SOC 2 Audit"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the audit scope..."
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="audit_type">Audit Type *</Label>
              <Select 
                value={formData.audit_type} 
                onValueChange={(v) => setFormData({...formData, audit_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select audit type" />
                </SelectTrigger>
                <SelectContent>
                  {AUDIT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Audit ID will be: {formData.audit_type === 'internal' ? 'IA' : 'EA'}-[FRAMEWORK]-[YEAR]-[SEQ]
              </p>
            </div>
            
            <div>
              <Label htmlFor="framework">Framework *</Label>
              <Select 
                value={formData.framework_id} 
                onValueChange={(v) => setFormData({...formData, framework_id: v})}
              >
                <SelectTrigger data-testid="framework-select-trigger">
                  <SelectValue placeholder="Select a framework" />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {frameworks.map((fw) => (
                    <SelectItem key={fw.id} value={fw.id}>
                      <span className="inline-flex items-center gap-2">
                        <span>{fw.name}</span>
                        <span className="text-xs text-gray-500">({fw.code} · {fw.total_controls || 0} ctrls)</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {frameworks.length} framework{frameworks.length === 1 ? '' : 's'} enabled for this tenant · enable more under Compliance → Frameworks
              </p>
            </div>
            
            <div>
              <Label htmlFor="scheduled_date">Scheduled Date *</Label>
              <Input
                id="scheduled_date"
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="create_engagement"
                checked={formData.create_engagement}
                onChange={(e) => setFormData({...formData, create_engagement: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="create_engagement" className="text-sm font-normal">
                Also create an Audit Engagement for tracking findings
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAudit}>
              Schedule Audit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Auditor Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              Invite Auditor
            </DialogTitle>
            <DialogDescription>
              {selectedAudit?.title ? `Invite an auditor to "${selectedAudit.title}"` : 'Add an auditor to this scheduled audit'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={inviteType} onValueChange={setInviteType} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="existing" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Existing User
              </TabsTrigger>
              <TabsTrigger value="external" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                External Auditor
              </TabsTrigger>
            </TabsList>

            <TabsContent value="existing" className="space-y-4 mt-4">
              <div>
                <Label>Search Users</Label>
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    loadAvailableUsers(e.target.value);
                  }}
                />
              </div>
              
              {availableUsers.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  {availableUsers.map(user => (
                    <div 
                      key={user.id}
                      className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                        inviteData.auditor_user_id === user.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setInviteData({
                        ...inviteData,
                        auditor_user_id: user.id,
                        auditor_email: user.email,
                        auditor_name: user.full_name
                      })}
                    >
                      <p className="font-medium">{user.full_name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="external" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="ext_email">Email Address *</Label>
                <Input
                  id="ext_email"
                  type="email"
                  value={inviteData.auditor_email}
                  onChange={(e) => setInviteData({...inviteData, auditor_email: e.target.value})}
                  placeholder="auditor@company.com"
                />
              </div>
              <div>
                <Label htmlFor="ext_name">Full Name</Label>
                <Input
                  id="ext_name"
                  value={inviteData.auditor_name}
                  onChange={(e) => setInviteData({...inviteData, auditor_name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <p className="text-sm text-gray-500">
                An invitation link will be generated for the external auditor to join.
              </p>
            </TabsContent>
          </Tabs>

          <div className="mt-4">
            <Label>Auditor Role</Label>
            <Select 
              value={inviteData.role} 
              onValueChange={(v) => setInviteData({...inviteData, role: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDITOR_ROLES.map(role => (
                  <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => {
              setShowInviteDialog(false);
              resetInviteForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleInviteAuditor}>
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduleAuditPage;
